import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { listPedagogicalSheets } from '@/data/services/pedagogicalSheetServices';
import {
  createStudentAssessment,
  updateStudentAssessment,
} from '@/data/services/studentAssessmentsServices';
import { OccurrenceRecord } from '@/utils/assessments/klassPedagogicalSheetRecordTypes';
import { occurrenceFieldsToDTO } from '@/utils/assessments/occurrenceFieldsToDTO';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import OccurrenceForm, { OccurrenceFormFields } from './OccurrenceForm';
import { variant } from './OccurrenceForm';

export type OccurrenceFormServiceContext =
  | { klassId: number; scheduledLessonId?: number; studentId?: number }
  | { studentId: number; klassId?: never; scheduledLessonId?: never };

export type OccurrenceFormServiceProps = {
  record?: OccurrenceRecord;
  onSuccess: () => Promise<void> | void;
  onCancel: () => void;
  variant?: variant;
} & OccurrenceFormServiceContext;

export default function OccurrenceFormService(
  props: OccurrenceFormServiceProps,
) {
  const { record, onSuccess, onCancel } = props;

  const { t } = useTranslation('translation', {
    keyPrefix: 'klassDetail.pedagogicalSheet.forms',
  });
  const { t: tOccurrence } = useTranslation('translation', {
    keyPrefix: 'occurrenceAssessmentForm',
  });

  const assessment = record?.assessment;

  const handleCreate = async (data: OccurrenceFormFields) => {
    const sheets = await listPedagogicalSheets({
      studentId: props.studentId ?? data.studentId ?? undefined,
    });
    const pedagogicalSheetId = sheets.results.at(0)?.id;

    if (!pedagogicalSheetId) {
      alert.error(t('sheetNotFound'));
      return false;
    }

    await createStudentAssessment(
      occurrenceFieldsToDTO(data, {
        pedagogicalSheetId,
        klassId: props.klassId,
        scheduledLessonId: props.scheduledLessonId,
      }),
    );
    alert.success(tOccurrence('successCreatedMessage'));
    return true;
  };

  const handleUpdate = async ({ description }: OccurrenceFormFields) => {
    if (!assessment) throw new Error('Assessment not found');
    await updateStudentAssessment({
      assessmentId: assessment.id,
      body: { occurrenceAssessment: { description } },
    });
    alert.success(tOccurrence('successUpdatedMessage'));
    return true;
  };

  const handleSubmit = (data: OccurrenceFormFields) =>
    assessment ? handleUpdate(data) : handleCreate(data);

  const { mutate: onSubmit, isPending } = useMutation(handleSubmit, {
    onSuccess: async isSaved => {
      if (isSaved) await onSuccess();
    },
    onError: (error: unknown) => alert.error(getErrorMessage(error)),
  });

  return (
    <OccurrenceForm
      klassId={props.klassId}
      studentId={props.studentId}
      record={record}
      isLoading={isPending}
      onSubmit={onSubmit}
      onCancel={onCancel}
      variant={props.variant ?? 'default'}
    />
  );
}
